import api from './api';
import { 
  Session, 
  University, 
  Campus, 
  Course,
  Country,
  UniversityPortalStats,
  SessionFormData,
  UniversityFormData,
  CampusFormData,
  CourseFormData,
  CountryFormData
} from '../types/UniversityPortal';

export const universityPortalApi = {
  // ==================== SESSIONS ====================
  getSessions: async (): Promise<Session[]> => {
    const response = await api.get('/university-portal/sessions');
    return response.data.data;
  },

  createSession: async (data: SessionFormData): Promise<Session> => {
    const response = await api.post('/university-portal/sessions', data);
    return response.data.data;
  },

  updateSession: async (id: number, data: Partial<SessionFormData>): Promise<Session> => {
    const response = await api.put(`/university-portal/sessions/${id}`, data);
    return response.data.data;
  },

  deleteSession: async (id: number): Promise<void> => {
    await api.delete(`/university-portal/sessions/${id}`);
  },

  // ==================== UNIVERSITIES ====================
  getUniversities: async (): Promise<University[]> => {
    const response = await api.get('/university-portal/universities');
    return response.data.data;
  },

  createUniversity: async (data: UniversityFormData): Promise<University> => {
    const response = await api.post('/university-portal/universities', data);
    return response.data.data;
  },

  updateUniversity: async (id: number, data: Partial<UniversityFormData>): Promise<University> => {
    const response = await api.put(`/university-portal/universities/${id}`, data);
    return response.data.data;
  },

  deleteUniversity: async (id: number): Promise<void> => {
    await api.delete(`/university-portal/universities/${id}`);
  },

  // ==================== CAMPUSES ====================
  getCampuses: async (): Promise<Campus[]> => {
    const response = await api.get('/university-portal/campuses');
    return response.data.data;
  },

  getCampusesByUniversity: async (universityId: number): Promise<Campus[]> => {
    const response = await api.get(`/university-portal/universities/${universityId}/campuses`);
    return response.data.data;
  },

  createCampus: async (data: CampusFormData): Promise<Campus> => {
    const response = await api.post('/university-portal/campuses', data);
    return response.data.data;
  },

  updateCampus: async (id: number, data: Partial<CampusFormData>): Promise<Campus> => {
    const response = await api.put(`/university-portal/campuses/${id}`, data);
    return response.data.data;
  },

  deleteCampus: async (id: number): Promise<void> => {
    await api.delete(`/university-portal/campuses/${id}`);
  },

  // ==================== COURSES ====================
  getCourses: async (): Promise<Course[]> => {
    const response = await api.get('/university-portal/courses');
    return response.data.data;
  },

  getCoursesByUniversity: async (universityId: number): Promise<Course[]> => {
    const response = await api.get(`/university-portal/universities/${universityId}/courses`);
    return response.data.data;
  },

  createCourse: async (data: CourseFormData): Promise<Course> => {
    const response = await api.post('/university-portal/courses', data);
    return response.data.data;
  },

  updateCourse: async (id: number, data: Partial<CourseFormData>): Promise<Course> => {
    const response = await api.put(`/university-portal/courses/${id}`, data);
    return response.data.data;
  },

  deleteCourse: async (id: number): Promise<void> => {
    await api.delete(`/university-portal/courses/${id}`);
  },

  // ==================== COUNTRIES ====================
  getCountries: async (): Promise<Country[]> => {
    const response = await api.get('/university-portal/countries');
    return response.data.data;
  },

  getCountryById: async (id: number): Promise<Country> => {
    const response = await api.get(`/university-portal/countries/${id}`);
    return response.data.data;
  },

  createCountry: async (data: CountryFormData): Promise<Country> => {
    const response = await api.post('/university-portal/countries', data);
    return response.data.data;
  },

  updateCountry: async (id: number, data: Partial<CountryFormData>): Promise<Country> => {
    const response = await api.put(`/university-portal/countries/${id}`, data);
    return response.data.data;
  },

  deleteCountry: async (id: number): Promise<void> => {
    await api.delete(`/university-portal/countries/${id}`);
  },

  // ==================== DASHBOARD ====================
  getDashboardStats: async (): Promise<UniversityPortalStats> => {
    const response = await api.get('/university-portal/dashboard');
    return response.data.data;
  },

  // ==================== RELATED DATA ====================
  getUniversityRelatedCounts: async (universityId: number): Promise<{campuses: number, courses: number}> => {
    const response = await api.get(`/university-portal/universities/${universityId}/related-counts`);
    return response.data.data;
  },

  getUniversityCampuses: async (universityId: number): Promise<Campus[]> => {
    const response = await api.get(`/university-portal/universities/${universityId}/campuses`);
    return response.data.data;
  },

  getUniversityCourses: async (universityId: number): Promise<Course[]> => {
    const response = await api.get(`/university-portal/universities/${universityId}/courses`);
    return response.data.data;
  }
};
